// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useEvolveDelete, type TableViewId, type TableViewType, useEvolvePost, useEvolveGet } from '@evolvemep/api-ts';

import type { LOCAL_STORAGE_TYPE } from 'constants/globalConstants';
import type { AgGridExportFormat } from 'helpers/ag-grid/Exports/useExportGrid';
import { isNil, isNotNil } from 'helpers/isNotNil';
import {
  parseUserPreferenceValueJson,
  stringifyUserPreferenceValueJson,
  toApiPreferenceKey,
} from 'hooks/typeCacheHooks/useUserPreferences/utils';
import { useEvolveGetWithLocalStorageContext } from 'hooks/useEvolveGetWithLocalStorage';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useMaybeUser } from 'hooks/user/useMaybeUser';

/** Keys and value types for user preferences, i.e. {[PREFERENCE_KEY]: PREFERENCE_VALUE_TYPE} */
export type USER_PREFERENCE_TYPE = {
  AD_SPOT_DISMISSED: boolean;
  NAV_BAR_EXPANDED: boolean;
  PREFERRED_EXPORT_FORMAT: AgGridExportFormat;

  // Computed keys:
  [key: `DEFAULT_TABLE_VIEW_ID_${TableViewType}`]: TableViewId | null;
  [key: `WELCOME_SCREEN_${string}`]: boolean;
};

export type UserPreferenceKey = keyof USER_PREFERENCE_TYPE;
export type UserPreferenceValue<TPreferenceKey extends UserPreferenceKey> = USER_PREFERENCE_TYPE[TPreferenceKey];

export const useUserPreferences = <TPreferenceKey extends UserPreferenceKey>(
  preferenceKey: TPreferenceKey,
  initialValue: UserPreferenceValue<TPreferenceKey>,
  lazy = false,
) => {
  const { user } = useMaybeUser();
  const isLoggedIn = isNotNil(user);
  const { alreadyPulled } = useEvolveGetWithLocalStorageContext();

  /** The key as the API sees it, both here and in the upsert below. */
  const apiPreferenceKey = toApiPreferenceKey(preferenceKey);

  const { apiCall: getUserPreferenceApiCall, loading } = useEvolveGet(
    `admin/userPreference/${encodeURIComponent(apiPreferenceKey)}`,
  );

  const storageKey = `USER_PREFERENCE_${preferenceKey}` as const;
  const [storedPreferenceValue, setStoredPreferenceValue, hasStoredValue] = useLocalStorage(
    storageKey,
    initialValue as LOCAL_STORAGE_TYPE[`USER_PREFERENCE_${TPreferenceKey}`],
  );
  const hasUserPreference = useMemo(
    () => hasStoredValue && !isNil(storedPreferenceValue),
    [hasStoredValue, storedPreferenceValue],
  );

  /**
   * The key this hook has most recently finished resolving,
   * either by fetching it or by determining that no fetch will happen.
   * Tracked as the key rather than a boolean so that a change to `preferenceKey`
   * starts over instead of reporting the previous key's result.
   */
  const [resolvedKey, setResolvedKey] = useState<typeof storageKey | null>(null);
  const resolved = resolvedKey === storageKey;

  // Update the local storage value on mount (if lazy isn't set).
  useEffect(() => {
    // Bail out if this is lazy executed, the user is not logged in, or if there is a pending or fulfilled fetch.
    if (lazy || !isLoggedIn || alreadyPulled.current[storageKey] === true) {
      setResolvedKey(storageKey);
      return;
    }
    // We need to mutate this value to mark that we've already pulled this preference.
    // oxlint-disable-next-line react/immutability
    alreadyPulled.current[storageKey] = true;
    /* `apiCall` thinks that you may need to pass in args
     * in the case that `URL` is a parameterized URL missing its params.
     * However, we've excluded those from `URL`
     * so any params have already been passed into the `useEvolveGet` above.
     **/
    // oxlint-disable-next-line typescript/no-explicit-any
    void getUserPreferenceApiCall(...([] as any))
      .then((userPreference) => {
        /*
         * This prevents a serious bug that can occur when we cache an HTML file,
         * which can happen if our CDN serves the wrong files.
         *
         * If an HTML value is cached when we're expecting JSON, it will crash the entire app,
         * and prevent the cache from being evicted, which causes an infinite crash loop.
         */
        if (typeof userPreference === 'string' && (userPreference as string).startsWith('<')) {
          throw new Error('Invalid HTML response');
        }

        const preferenceValue = parseUserPreferenceValueJson<TPreferenceKey>(userPreference?.preferenceValue);
        setStoredPreferenceValue((preferenceValue ?? initialValue) as typeof storedPreferenceValue);
      })
      .catch(() => {
        alreadyPulled.current[storageKey] = false;
      })
      .finally(() => setResolvedKey(storageKey));
  }, [storageKey, isLoggedIn, alreadyPulled, getUserPreferenceApiCall, setStoredPreferenceValue, lazy, initialValue]);

  const { apiCall: upsertUserPreferenceApiCall, loading: updatingUserPreference } = useEvolvePost(
    `admin/userPreference`,
    { alertOnError: false },
  );

  const setUserPreference = useCallback(
    async (updatedPreferenceValue: USER_PREFERENCE_TYPE[TPreferenceKey]) => {
      await upsertUserPreferenceApiCall({
        preferenceKey: apiPreferenceKey,
        preferenceValue: stringifyUserPreferenceValueJson(updatedPreferenceValue),
      });
    },
    [apiPreferenceKey, upsertUserPreferenceApiCall],
  );

  const { apiCall: deleteUserPreferenceApiCall, loading: deletingUserPreference } = useEvolveDelete(
    'admin/userPreference/:preferenceKey',
  );
  const deleteUserPreference = useCallback(async () => {
    await deleteUserPreferenceApiCall({
      url: `admin/userPreference/${encodeURIComponent(apiPreferenceKey)}`,
    });
  }, [apiPreferenceKey, deleteUserPreferenceApiCall]);

  const onChangeAsync = useCallback(
    async (
      value:
        | UserPreferenceValue<TPreferenceKey>
        | ((previousPreferenceValue: UserPreferenceValue<TPreferenceKey>) => UserPreferenceValue<TPreferenceKey>),
    ) => {
      const prevPreferenceValue = storedPreferenceValue;
      const newPreferenceValue =
        typeof value === 'function' ? value(prevPreferenceValue as UserPreferenceValue<TPreferenceKey>) : value;
      // Optimistic update.
      setStoredPreferenceValue(value as typeof storedPreferenceValue);
      try {
        if (isNil(newPreferenceValue)) {
          if (hasUserPreference) {
            await deleteUserPreference();
          }
        } else {
          await setUserPreference(newPreferenceValue);
        }
      } catch {
        // Revert optimistic update on error.
        setStoredPreferenceValue(prevPreferenceValue);
      }
    },
    [hasUserPreference, storedPreferenceValue, setStoredPreferenceValue, setUserPreference, deleteUserPreference],
  );
  // `onChange` should not be async; callers should expect the update to work
  const onChange = useCallback(
    (...props: Parameters<typeof onChangeAsync>) => {
      void onChangeAsync(...props);
    },
    [onChangeAsync],
  );

  const mutationRunning = updatingUserPreference || deletingUserPreference;

  return [
    storedPreferenceValue as UserPreferenceValue<TPreferenceKey>,
    onChange,
    (loading || !resolved) && !hasUserPreference,
    { mutationRunning },
  ] as const;
};
